// src/components/vcard/VCardForm.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CheckCircle2,
  AlertCircle,
  Plane,
  BaggageClaim,
  ChevronDown,
  ChevronRight,
  ScrollText,
  ShieldCheck,
} from "lucide-react";
import { useVCardForm } from "../../hooks/useVCardForm";
import CategoryFields from "./CategoryFields";
import QRCodePreview from "./QRCodePreview";
import ProtectedDocumentUploadPanel from "./attachments/ProtectedDocumentUploadPanel";
import {
  getBusinessWarrantyFiles,
  persistBusinessWarrantyFiles,
  withBusinessWarrantyFiles,
} from "./attachments/businessWarrantyFiles";
import {
  getExtremeKitWarrantyFiles,
  persistExtremeKitWarrantyFiles,
  withExtremeKitWarrantyFiles,
} from "./attachments/extremeKitWarrantyFiles";
import {
  getExtremeKitHealthRecordsFiles,
  persistExtremeKitHealthRecordsFiles,
  withExtremeKitHealthRecordsFiles,
} from "./attachments/extremeKitHealthRecordsFiles";
import {
  getMyTechWarrantyFiles,
  persistMyTechWarrantyFiles,
  withMyTechWarrantyFiles,
} from "./attachments/myTechWarrantyFiles";
import {
  getLuggageIdentityDocumentsFiles,
  persistLuggageIdentityDocumentsFiles,
  withLuggageIdentityDocumentsFiles,
} from "./attachments/luggageIdentityDocumentsFiles";
import {
  getLuggageTravelItineraryFiles,
  persistLuggageTravelItineraryFiles,
  withLuggageTravelItineraryFiles,
} from "./attachments/luggageTravelItineraryFiles";
import {
  getHomeWarrantyFiles,
  persistHomeWarrantyFiles,
  withHomeWarrantyFiles,
} from "./attachments/homeWarrantyFiles";
import VCardMessagePanel from "./shared/VCardMessagePanel";
import { SMARTTAG_LAYOUT_STANDARD } from "./shared/vcardLayoutStandard";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import UnsavedChangesDialog from "../ui/UnsavedChangesDialog";
import type {
  SmartTagAttachmentFile,
  VCard,
  VCardCategory,
} from "../../types/vcard";

interface VCardFormProps {
  category: VCardCategory;
}

const normalizeForSnapshot = (value: unknown): unknown => {
  if (value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeForSnapshot(item));
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "object") {
    const normalizedEntries = Object.entries(value as Record<string, unknown>)
      .filter(
        ([, entryValue]) =>
          entryValue !== undefined && typeof entryValue !== "function",
      )
      .map(
        ([key, entryValue]) => [key, normalizeForSnapshot(entryValue)] as const,
      )
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

    return Object.fromEntries(normalizedEntries);
  }

  return value;
};

const serializeFormData = (data: Partial<VCard>): string =>
  JSON.stringify(normalizeForSnapshot(data));

const STANDARD_LAYOUT_CATEGORIES: readonly VCardCategory[] = [
  "home",
  "luggage",
  "myTech",
  "extremeKit",
  "business",
];

const VCardForm: React.FC<VCardFormProps> = ({ category }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [currentSnapshot, setCurrentSnapshot] = useState("");
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isSnapshotInitialized, setIsSnapshotInitialized] = useState(false);
  const [isFlightScheduleOpen, setIsFlightScheduleOpen] = useState(false);
  const [isTravelItineraryOpen, setIsTravelItineraryOpen] = useState(false);
  const categoryRef = useRef(category);
  const contentRef = useRef<HTMLDivElement>(null);
  const saveAndLeaveRef = useRef(false);
  const [isSaveAndLeavePending, setIsSaveAndLeavePending] = useState(false);

  const {
    formData,
    handleChange,
    handleSubmit: onSubmit,
    setFormData,
    isFormDataReady,
  } = useVCardForm({
    category,
  });
  const formSnapshot = useMemo(() => serializeFormData(formData), [formData]);
  const myTechWarrantyFiles = useMemo(
    () => getMyTechWarrantyFiles(formData),
    [formData],
  );
  const extremeKitWarrantyFiles = useMemo(
    () => getExtremeKitWarrantyFiles(formData),
    [formData],
  );
  const businessWarrantyFiles = useMemo(
    () => getBusinessWarrantyFiles(formData),
    [formData],
  );
  const extremeKitHealthRecordsFiles = useMemo(
    () => getExtremeKitHealthRecordsFiles(formData),
    [formData],
  );
  const luggageTravelItineraryFiles = useMemo(
    () => getLuggageTravelItineraryFiles(formData),
    [formData],
  );
  const homeWarrantyFiles = useMemo(
    () => getHomeWarrantyFiles(formData),
    [formData],
  );
  const luggageIdentityDocumentsFiles = useMemo(
    () => getLuggageIdentityDocumentsFiles(formData),
    [formData],
  );
  const isDirty = useMemo(
    () =>
      Boolean(
        isSnapshotInitialized &&
        hasUserInteracted &&
        currentSnapshot !== initialSnapshot,
      ),
    [
      currentSnapshot,
      hasUserInteracted,
      initialSnapshot,
      isSnapshotInitialized,
    ],
  );
  const { isPromptOpen, stayOnPage, leavePage } = useUnsavedChangesGuard(
    isDirty && !isSubmitting,
  );

  const submitCurrentForm = useCallback(() => {
    const form = contentRef.current?.querySelector("form");
    if (!form) {
      return false;
    }

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit();
    } else {
      form.dispatchEvent(
        new Event("submit", { bubbles: true, cancelable: true }),
      );
    }
    return true;
  }, []);

  const handleStay = useCallback(() => {
    saveAndLeaveRef.current = false;
    setIsSaveAndLeavePending(false);
    stayOnPage();
  }, [stayOnPage]);

  const handleLeaveWithoutSaving = useCallback(() => {
    saveAndLeaveRef.current = false;
    setIsSaveAndLeavePending(false);
    leavePage();
  }, [leavePage]);

  const handleSaveAndLeave = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    saveAndLeaveRef.current = true;
    setIsSaveAndLeavePending(true);
    const didSubmit = submitCurrentForm();
    if (!didSubmit) {
      saveAndLeaveRef.current = false;
      setIsSaveAndLeavePending(false);
    }
  }, [isSubmitting, submitCurrentForm]);

  const markUserInteracted = useCallback(
    (event: React.FormEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const checkboxInput = target.closest("input[type='checkbox']");
      if (checkboxInput) {
        return;
      }

      setHasUserInteracted(true);
    },
    [],
  );

  const handleInteractionClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const button = target.closest("button");
      if (!button) {
        return;
      }

      const buttonType = button.getAttribute("type") || "submit";
      if (buttonType !== "submit") {
        setHasUserInteracted(true);
      }
    },
    [],
  );

  useEffect(() => {
    if (categoryRef.current === category) {
      return;
    }

    categoryRef.current = category;
    setInitialSnapshot("");
    setCurrentSnapshot("");
    setHasUserInteracted(false);
    setIsSnapshotInitialized(false);
    saveAndLeaveRef.current = false;
    setIsSaveAndLeavePending(false);
  }, [category]);

  useEffect(() => {
    if (isPromptOpen) {
      return;
    }
    saveAndLeaveRef.current = false;
    setIsSaveAndLeavePending(false);
  }, [isPromptOpen]);

  useEffect(() => {
    if (!isFormDataReady) {
      return;
    }

    if (!isSnapshotInitialized) {
      setInitialSnapshot(formSnapshot);
      setCurrentSnapshot(formSnapshot);
      setIsSnapshotInitialized(true);
      return;
    }

    if (!hasUserInteracted) {
      setInitialSnapshot(formSnapshot);
    }

    setCurrentSnapshot(formSnapshot);
  }, [formSnapshot, hasUserInteracted, isFormDataReady, isSnapshotInitialized]);

  // Clear success message after 20 seconds
  useEffect(() => {
    if (submitStatus?.type === "success") {
      const timer = setTimeout(() => {
        setSubmitStatus(null);
      }, 20000); // 20 seconds

      // Cleanup timer on component unmount or when submitStatus changes
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setSubmitStatus(null);

      await onSubmit(e);
      const savedSnapshot = serializeFormData(formData);
      setInitialSnapshot(savedSnapshot);
      setCurrentSnapshot(savedSnapshot);
      setHasUserInteracted(false);
      setIsSnapshotInitialized(true);
      const shouldLeaveAfterSave = saveAndLeaveRef.current;
      saveAndLeaveRef.current = false;
      setIsSaveAndLeavePending(false);

      setSubmitStatus({
        type: "success",
        message: "SmartTag saved successfully!",
      });

      if (shouldLeaveAfterSave) {
        leavePage();
      }
    } catch (error) {
      saveAndLeaveRef.current = false;
      setIsSaveAndLeavePending(false);
      setSubmitStatus({
        type: "error",
        message:
          error instanceof Error ? error.message : "Failed to save SmartTag",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageChange = useCallback(
    (value: string) => {
      setHasUserInteracted(true);
      setFormData((prev) => ({
        ...prev,
        note: value,
      }));
    },
    [setFormData],
  );

  const handleMessageSave = useCallback(() => {
    const didSubmit = submitCurrentForm();
    if (!didSubmit) {
      setSubmitStatus({
        type: "error",
        message: "Could not find a form to save this SmartTag message.",
      });
    }
  }, [submitCurrentForm]);

  const handleMyTechWarrantyFilesChange = useCallback(
    async (files: SmartTagAttachmentFile[]) => {
      setHasUserInteracted(true);
      setFormData((prev) => withMyTechWarrantyFiles(prev, files));

      // Auto-persist warranty files to Firestore as a scoped field write,
      // independent of the form's Save button. This eliminates the orphan
      // class where the user uploaded a file (bytes already in Storage)
      // and then navigated away without saving the form, losing the
      // Firestore index of what's attached. Storage-as-truth + auto-sync.
      const userId = formData.userId;
      if (userId) {
        try {
          await persistMyTechWarrantyFiles(userId, files);
        } catch (error) {
          // Surface as a non-fatal log; the next Storage reconcile on
          // mount will reconverge state. Don't block the upload UI.
          console.warn(
            "Could not auto-persist myTech warranty file metadata",
            error,
          );
        }
      }
    },
    [formData.userId, setFormData],
  );
  const handleExtremeKitWarrantyFilesChange = useCallback(
    async (files: SmartTagAttachmentFile[]) => {
      setHasUserInteracted(true);
      setFormData((prev) => withExtremeKitWarrantyFiles(prev, files));

      const userId = formData.userId;
      if (userId) {
        try {
          await persistExtremeKitWarrantyFiles(userId, files);
        } catch (error) {
          console.warn(
            "Could not auto-persist extremeKit warranty file metadata",
            error,
          );
        }
      }
    },
    [formData.userId, setFormData],
  );
  const handleBusinessWarrantyFilesChange = useCallback(
    async (files: SmartTagAttachmentFile[]) => {
      setHasUserInteracted(true);
      setFormData((prev) => withBusinessWarrantyFiles(prev, files));

      const userId = formData.userId;
      if (userId) {
        try {
          await persistBusinessWarrantyFiles(userId, files);
        } catch (error) {
          console.warn(
            "Could not auto-persist business warranty file metadata",
            error,
          );
        }
      }
    },
    [formData.userId, setFormData],
  );
  const handleExtremeKitHealthRecordsFilesChange = useCallback(
    async (files: SmartTagAttachmentFile[]) => {
      setHasUserInteracted(true);
      setFormData((prev) => withExtremeKitHealthRecordsFiles(prev, files));

      const userId = formData.userId;
      if (userId) {
        try {
          await persistExtremeKitHealthRecordsFiles(userId, files);
        } catch (error) {
          console.warn(
            "Could not auto-persist extremeKit health records file metadata",
            error,
          );
        }
      }
    },
    [formData.userId, setFormData],
  );
  const handleLuggageTravelItineraryFilesChange = useCallback(
    async (files: SmartTagAttachmentFile[]) => {
      setHasUserInteracted(true);
      setFormData((prev) => withLuggageTravelItineraryFiles(prev, files));

      const userId = formData.userId;
      if (userId) {
        try {
          await persistLuggageTravelItineraryFiles(userId, files);
        } catch (error) {
          console.warn(
            "Could not auto-persist luggage travel itinerary file metadata",
            error,
          );
        }
      }
    },
    [formData.userId, setFormData],
  );
  const handleHomeWarrantyFilesChange = useCallback(
    async (files: SmartTagAttachmentFile[]) => {
      setHasUserInteracted(true);
      setFormData((prev) => withHomeWarrantyFiles(prev, files));

      const userId = formData.userId;
      if (userId) {
        try {
          await persistHomeWarrantyFiles(userId, files);
        } catch (error) {
          console.warn(
            "Could not auto-persist home warranty file metadata",
            error,
          );
        }
      }
    },
    [formData.userId, setFormData],
  );
  const handleLuggageIdentityDocumentsFilesChange = useCallback(
    async (files: SmartTagAttachmentFile[]) => {
      setHasUserInteracted(true);
      setFormData((prev) => withLuggageIdentityDocumentsFiles(prev, files));

      const userId = formData.userId;
      if (userId) {
        try {
          await persistLuggageIdentityDocumentsFiles(userId, files);
        } catch (error) {
          console.warn(
            "Could not auto-persist luggage identity document file metadata",
            error,
          );
        }
      }
    },
    [formData.userId, setFormData],
  );

  const messageStatus = useMemo(() => {
    if (!submitStatus) {
      return null;
    }

    if (submitStatus.type === "success") {
      return {
        type: "success" as const,
        text: "Message saved to vCard.",
      };
    }

    return {
      type: "error" as const,
      text: submitStatus.message,
    };
  }, [submitStatus]);

  const isStandardLayout = STANDARD_LAYOUT_CATEGORIES.includes(category);
  const shouldShowRightColumnContent = category !== "home";
  const isLuggageCategory = category === "luggage";
  const isMyTechCategory = category === "myTech";
  const isExtremeKitCategory = category === "extremeKit";
  const isBusinessCategory = category === "business";
  const isHomeCategory = category === "home";
  const leftColumnClassName = isStandardLayout
    ? SMARTTAG_LAYOUT_STANDARD.leftColumnCardClass
    : "";
  const layoutClassName = isStandardLayout
    ? `${SMARTTAG_LAYOUT_STANDARD.gridClass} ${SMARTTAG_LAYOUT_STANDARD.layoutSurfaceClass}`
    : shouldShowRightColumnContent
      ? "grid grid-cols-1 gap-6 md:grid-cols-[1.1fr_0.9fr]"
      : "";

  return (
    <>
      <UnsavedChangesDialog
        isOpen={isPromptOpen}
        message="You have unsaved SmartTag edits. Leave this page without saving?"
        onSaveAndLeave={handleSaveAndLeave}
        onStay={handleStay}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
        isSaveAndLeavePending={isSaveAndLeavePending}
        disableActions={isSubmitting}
      />

      <div
        ref={contentRef}
        className={layoutClassName}
        onChangeCapture={markUserInteracted}
        onClickCapture={handleInteractionClickCapture}
      >
        <div className={leftColumnClassName}>
          {submitStatus && (
            <div
              className={`mb-6 flex items-center space-x-2 rounded-lg p-4 ${
                submitStatus.type === "success"
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              {submitStatus.type === "success" ? (
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
              )}
              <div className="flex-1">
                {submitStatus.message.split("\n").map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            </div>
          )}

          <CategoryFields
            category={category}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            showSaveConfirmation={submitStatus?.type === "success"}
          />
        </div>

        {shouldShowRightColumnContent ? (
          <div className={SMARTTAG_LAYOUT_STANDARD.rightColumnClass}>
            <div className={SMARTTAG_LAYOUT_STANDARD.qrCardClass}>
              <h2 className="mb-6 text-lg font-semibold text-gray-900 dark:text-gray-100">
                QR Code Preview
              </h2>
              <QRCodePreview key={category} category={category} />
            </div>

            <VCardMessagePanel
              value={formData.note || ""}
              onChange={handleMessageChange}
              onSave={handleMessageSave}
              isSaving={isSubmitting}
              status={messageStatus}
              title="Message For Finder"
              description="Write a short message that appears on this SmartTag's public vCard when the QR code is scanned."
              saveLabel="Save Message"
            />

            {isMyTechCategory && (
              <ProtectedDocumentUploadPanel
                userId={formData.userId || ""}
                vcardCategory="myTech"
                attachmentKey="warranty"
                files={myTechWarrantyFiles}
                onFilesChange={handleMyTechWarrantyFilesChange}
                disabled={isSubmitting || !formData.userId}
                title="Warranty & Proof of Purchase"
                description="Upload private receipts, warranty cards, or ownership documents for this tech tag."
              />
            )}
            {isExtremeKitCategory && (
              <>
                <ProtectedDocumentUploadPanel
                  userId={formData.userId || ""}
                  vcardCategory="extremeKit"
                  attachmentKey="warranty"
                  files={extremeKitWarrantyFiles}
                  onFilesChange={handleExtremeKitWarrantyFilesChange}
                  disabled={isSubmitting || !formData.userId}
                  title="Warranty & Proof of Purchase"
                  description="Upload private receipts, warranty cards, or ownership documents for this kit tag."
                />
                <ProtectedDocumentUploadPanel
                  userId={formData.userId || ""}
                  vcardCategory="extremeKit"
                  attachmentKey="healthRecords"
                  files={extremeKitHealthRecordsFiles}
                  onFilesChange={handleExtremeKitHealthRecordsFilesChange}
                  disabled={isSubmitting || !formData.userId}
                  title="Health Records, X-Rays etc"
                  description={
                    <span className="inline-flex flex-wrap items-center gap-1">
                      <span>
                        Upload private health records, X-rays, and supporting
                        emergency documents. Planned feature: this block will be
                        protected by{" "}
                        <span className="inline-flex items-center gap-1">
                          <span>2FA</span>
                          <ShieldCheck className="h-4 w-4 text-blue-600" />
                        </span>{" "}
                        in a later phase.
                      </span>
                    </span>
                  }
                />
              </>
            )}
            {isBusinessCategory && (
              <ProtectedDocumentUploadPanel
                userId={formData.userId || ""}
                vcardCategory="business"
                attachmentKey="warranty"
                files={businessWarrantyFiles}
                onFilesChange={handleBusinessWarrantyFilesChange}
                disabled={isSubmitting || !formData.userId}
                title="Certifications, Licences and Qualifications"
                description="Upload private certifications, licences, and qualification documents for this business tag."
              />
            )}
            {isLuggageCategory && (
              <>
                <div className={SMARTTAG_LAYOUT_STANDARD.qrCardClass}>
                  <button
                    type="button"
                    onClick={() => setIsFlightScheduleOpen((prev) => !prev)}
                    className="flex w-full items-center justify-between text-left"
                    aria-expanded={isFlightScheduleOpen}
                  >
                    <span className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                      <Plane className="h-5 w-5 text-red-500" />
                      <span>Flight Schedule</span>
                    </span>
                    {isFlightScheduleOpen ? (
                      <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>
                  {isFlightScheduleOpen && (
                    <>
                      <p className="mb-4 mt-3 text-sm text-gray-600 dark:text-gray-300">
                        Placeholder for upcoming flights shown on your travel
                        tag.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          "Flight Number",
                          "Date",
                          "Departure Time",
                          "Departure Airport",
                          "Destination",
                          "Arrival Time (Local)",
                        ].map((label) => (
                          <div
                            key={label}
                            className="rounded-md border border-gray-200 bg-white/70 p-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300"
                          >
                            <p className="font-medium text-gray-800 dark:text-gray-100">
                              {label}
                            </p>
                            <p className="mt-1 text-gray-500 dark:text-gray-400">
                              Placeholder
                            </p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className={SMARTTAG_LAYOUT_STANDARD.qrCardClass}>
                  <button
                    type="button"
                    onClick={() => setIsTravelItineraryOpen((prev) => !prev)}
                    className="flex w-full items-center justify-between text-left"
                    aria-expanded={isTravelItineraryOpen}
                  >
                    <span className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                      <BaggageClaim className="h-5 w-5 text-red-500" />
                      <span>Travel Itinerary</span>
                    </span>
                    {isTravelItineraryOpen ? (
                      <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>
                  {isTravelItineraryOpen && (
                    <>
                      <p className="mb-3 mt-3 text-sm text-gray-600 dark:text-gray-300">
                        Placeholder block for key trip steps and movement
                        details.
                      </p>
                      <div className="space-y-2 rounded-md border border-dashed border-gray-300 bg-white/60 p-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                        <p>- Segment and route notes</p>
                        <p>- Connection windows</p>
                        <p>- Transfer and pickup details</p>
                        <p>- Important time markers</p>
                      </div>
                    </>
                  )}
                </div>

                <ProtectedDocumentUploadPanel
                  userId={formData.userId || ""}
                  vcardCategory="luggage"
                  attachmentKey="travelItinerary"
                  files={luggageTravelItineraryFiles}
                  onFilesChange={handleLuggageTravelItineraryFilesChange}
                  disabled={isSubmitting || !formData.userId}
                  title={
                    <span className="flex items-center gap-2">
                      <Plane className="h-5 w-5 text-green-600" />
                      <BaggageClaim className="h-5 w-5 text-green-600" />
                      <span>Flights, Accommodation and Travel Documents</span>
                    </span>
                  }
                  description="Upload private flight, accommodation, travel, insurance, and policy documents for this luggage tag."
                />
                <ProtectedDocumentUploadPanel
                  userId={formData.userId || ""}
                  vcardCategory="luggage"
                  attachmentKey="identityDocuments"
                  files={luggageIdentityDocumentsFiles}
                  onFilesChange={handleLuggageIdentityDocumentsFilesChange}
                  disabled={isSubmitting || !formData.userId}
                  title={
                    <span className="flex items-center gap-2">
                      <ScrollText className="h-5 w-5 text-green-600" />
                      <span>
                        Passports, Birth, Marriage and Citizenship Documents
                      </span>
                    </span>
                  }
                  description={
                    <span className="inline-flex flex-wrap items-center gap-1">
                      <span>
                        Upload private passport and civil identity documents.
                        Planned feature: this block will be protected by{" "}
                        <span className="inline-flex items-center gap-1">
                          <span>2FA</span>
                          <ShieldCheck className="h-4 w-4 text-blue-600" />
                        </span>{" "}
                        in a later phase.
                      </span>
                    </span>
                  }
                />
              </>
            )}
          </div>
        ) : isStandardLayout ? (
          <div className={SMARTTAG_LAYOUT_STANDARD.rightColumnClass}>
            {isHomeCategory ? (
              <ProtectedDocumentUploadPanel
                userId={formData.userId || ""}
                vcardCategory="home"
                attachmentKey="warranty"
                files={homeWarrantyFiles}
                onFilesChange={handleHomeWarrantyFilesChange}
                disabled={isSubmitting || !formData.userId}
                title="Property Documents"
                description="Upload private property title, rates notices, rental agreements, home and contents insurance, termite treatment records, house plans, and related ownership documents."
              />
            ) : (
              <div aria-hidden="true" />
            )}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default VCardForm;
